import { ApplicationTypes } from "../store/application/store"
import { CavityWallData } from "../store/cavity-wall/store"
import { FlatRoofData } from "../store/flat-roof/store"
import { FloorData } from "../store/floor/store"
import { SlopedRoofData } from "../store/sloped-roof/store"
import { SolData } from "../store/sol/store"
import { ToitEnPenteData } from "../store/toit-en-pente/store"
import { VentilatedWallData } from "../store/ventilated-wall/store"
import { AccountService } from "./account.service"

export interface ConfigurationData {
  record_id?: string
  id?: string
  application_type: ApplicationTypes
  application_data:
    | FlatRoofData
    | CavityWallData
    | SlopedRoofData
    | VentilatedWallData
    | FloorData
    | SolData
    | ToitEnPenteData
  display_name: string
  language: string
  my_iko_id?: string
  project_id?: string
  configuration_url?: string
  created_at?: string
  updated_at?: string
}

export class ConfigurationService {
  /**
   * List all account configurations
   */
  static async listConfigurations(): Promise<{
    success: boolean
    configurations: ConfigurationData[]
  } | null> {
    try {
      const response = await AccountService.makeSecureRequest(
        "/api/configuration/list",
        {
          method: "POST",
        }
      )
      if (response.ok) {
        const responseData = await response.json()
        return responseData
      }
      return null
    } catch (error) {
      console.error("Failed to list configurations:", error)
      return null
    }
  }

  /**
   * Save a configuration
   */
  static async saveConfiguration(
    configuration: ConfigurationData
  ): Promise<{
    success: boolean
    configurations: ConfigurationData[]
    saved_configuration?: ConfigurationData | null
  } | null> {
    try {
      const csrfToken = await AccountService.getCsrfToken()
      const response = await AccountService.makeSecureRequest(
        "/api/configuration/save",
        {
          method: "POST",
          body: JSON.stringify({ configuration, csrfToken }),
        }
      )

      if (response.ok) {
        const responseData = await response.json()
        return responseData
      }

      return null
    } catch (error) {
      console.error("Failed to save configuration:", error)
      return null
    }
  }

  /**
   * Edit a configuration
   */
  static async editConfiguration(
    configuration: ConfigurationData
  ): Promise<{
    success: boolean
    configurations: ConfigurationData[]
  } | null> {
    try {
      const csrfToken = await AccountService.getCsrfToken()
      const response = await AccountService.makeSecureRequest(
        "/api/configuration/edit",
        {
          method: "POST",
          body: JSON.stringify({ configuration, csrfToken }),
        }
      )

      if (response.ok) {
        const responseData = await response.json()
        return responseData
      }

      return null
    } catch (error) {
      console.error("Failed to edit configuration:", error)
      return null
    }
  }

  /**
   * Delete a configuration
   */
  static async deleteConfiguration(
    configuration: ConfigurationData
  ): Promise<{
    success: boolean
    configurations: ConfigurationData[]
  } | null> {
    try {
      const csrfToken = await AccountService.getCsrfToken()
      const response = await AccountService.makeSecureRequest(
        "/api/configuration/delete",
        {
          method: "POST",
          body: JSON.stringify({ configuration, csrfToken }),
        }
      )

      if (response.ok) {
        const responseData = await response.json()
        return responseData
      }

      return null
    } catch (error) {
      console.error("Failed to delete configuration:", error)
      return null
    }
  }

  /**
   * Mint a fresh, short-lived design_token for a saved configuration. The token
   * is signed server-side on demand and is not persisted anywhere.
   */
  static async getDesignToken(recordId: string): Promise<string | null> {
    try {
      const csrfToken = await AccountService.getCsrfToken()
      const response = await AccountService.makeSecureRequest(
        "/api/configuration/design-token",
        {
          method: "POST",
          body: JSON.stringify({ record_id: recordId, csrfToken }),
        }
      )

      if (response.ok) {
        const responseData = await response.json()
        return responseData?.design_token || null
      }

      return null
    } catch (error) {
      console.error("Failed to create design token:", error)
      return null
    }
  }
}
